/**
 * Адаптер поручений: `/assignments*` канона → форма поручения прототипа.
 *
 * Экран поручений (`pages/ops/Assignments.tsx`) написан против
 * `OverlayAssignment` — записи локального слоя. На каноне поручение — запись
 * сервера (приложение `assignments`), и адаптер отдаёт ТУ ЖЕ форму плюс поле
 * `server` с тем, чего локальный слой не знал: номер записи, ключ ситуации,
 * следующий шаг глазами того, кто смотрит (право и независимость решает
 * сервер), дата снимка оснований.
 *
 * Чего адаптер не делает: не считает «что изменилось» (это пересчёт детектором
 * на сервере против снимка постановки) и не решает, можно ли нажать кнопку —
 * он только переносит ответ сервера. Отсутствие ≠ ноль: величина, которой
 * сервер не назвал (`after: null`), остаётся `null`.
 */

import {
  api,
  type AssignmentDiff,
  type AssignmentList,
  type AssignmentRow,
  type AssignmentStep,
  type QueryParams,
} from '../../../api/client';
import type { AssignmentEvent, AssignmentStatus, OverlayAssignment } from '../../auth/overlay';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  noAuthor: {
    id: 'data.assignments.noAuthor',
    defaultMessage: 'автор не назван',
    description: 'Подпись в истории поручения, когда сервер не назвал автора записи (строчная фраза)',
  },
  noNextStep: {
    id: 'data.assignments.noNextStep',
    defaultMessage: 'у поручения нет следующей ступени',
    description: 'Сообщение об ошибке, если нажали «дальше» у поручения, которое уже закрыто',
  },
});


/** Следующий шаг поручения — как его видит сервер для вошедшего. */
export interface ServerNextStep {
  status: AssignmentStatus | null;
  action: AssignmentStep | null;
  allowed: boolean;
  /** Почему нельзя — словами сервера. */
  refusal: string;
}

export interface ServerAssignment extends OverlayAssignment {
  server: {
    id: number;
    situationKey: string;
    /** Вид ситуации-основания (`overdue_material`, `water_gap_after_window`…). */
    kind: string;
    actorName: string | null;
    snapshotAsOf: string;
    snapshotDemo: boolean | null;
    /**
     * После срока на дату среза ответа — считает сервер. `null` — ответ его не
     * знает (прежний сервер): экран остаётся на запасном правиле (`isLate`).
     */
    overdue: boolean | null;
    next: ServerNextStep;
  };
}

export interface AssignmentsView {
  rows: ServerAssignment[];
  counts: Record<AssignmentStatus, number>;
  total: number;
  mayIssue: boolean;
  issueRefusal: string;
  note: string;
}

/** Событие журнала сервера → событие истории прототипа. */
export function toEvent(entry: AssignmentRow['journal'][number]): AssignmentEvent {
  return {
    date: entry.at.slice(0, 10),
    // Прототип называет постановку `created`, а переходы — именем состояния.
    kind: entry.action === 'create' ? 'created' : entry.status_after,
    by: entry.by ?? tr(m.noAuthor),
    note: entry.comment.trim() ? entry.comment : null,
  };
}

export function toAssignment(row: AssignmentRow): ServerAssignment {
  return {
    id: `asg-${row.id}`,
    title: row.title,
    body: row.body,
    // Ситуация канона — состояние ХОЗЯЙСТВА на дату: уровень поручения тот же.
    level: 'farm',
    ref: { farm_id: String(row.key_parts.actor_id) },
    assignee: row.assignee,
    due: row.due ?? null,
    status: row.status,
    created_by: row.created_by ?? tr(m.noAuthor),
    events: row.journal.map(toEvent),
    source: 'attention',
    source_id: row.situation_key,
    server: {
      id: row.id,
      situationKey: row.situation_key,
      kind: row.key_parts.kind,
      actorName: row.actor_name ?? null,
      snapshotAsOf: row.snapshot_as_of,
      snapshotDemo: row.snapshot_demo ?? null,
      overdue: typeof row.overdue === 'boolean' ? row.overdue : null,
      next: {
        status: row.next_step.status ?? null,
        action: (row.next_step.action as AssignmentStep | null | undefined) ?? null,
        allowed: row.next_step.allowed,
        refusal: row.next_step.refusal ?? '',
      },
    },
  };
}

export function toAssignmentsView(list: AssignmentList): AssignmentsView {
  const counts: Record<AssignmentStatus, number> = {
    open: 0,
    in_progress: 0,
    done: 0,
    checked: 0,
  };
  for (const row of list.by_status) counts[row.status] = row.count;
  return {
    rows: list.items.map(toAssignment),
    counts,
    total: list.total,
    mayIssue: list.may_issue,
    issueRefusal: list.issue_refusal ?? '',
    note: list.note,
  };
}

export async function loadAssignments(params: QueryParams): Promise<AssignmentsView> {
  return toAssignmentsView(await api.assignments(params));
}

/** Постановка из ситуации. CSRF берётся первым — без cookie токена нет. */
export async function issueAssignment(
  input: {
    situationKey: string;
    title: string;
    assignee: string;
    body?: string;
    due?: string | null;
    comment?: string;
  },
  params: QueryParams,
): Promise<number> {
  await api.csrf();
  const record = await api.issueAssignment(
    {
      situation_key: input.situationKey,
      title: input.title,
      assignee: input.assignee,
      body: input.body ?? '',
      due: input.due || null,
      comment: input.comment ?? '',
    },
    params,
  );
  return record.id;
}

/** Следующая ступень. Жест берётся у сервера, а не выводится на клиенте. */
export async function advanceServerAssignment(
  row: ServerAssignment,
  comment: string,
): Promise<void> {
  const step = row.server.next.action;
  if (!step) throw new Error(tr(m.noNextStep));
  await api.csrf();
  await api.advanceAssignment(row.server.id, step, comment.trim());
}

/** Одна строка таблицы «при создании / сейчас / разница». */
export interface EvidenceRow {
  field: string;
  label: string;
  before: number | null;
  after: number | null;
  delta: number | null;
}

export interface AssignmentEvidenceView {
  snapshotAsOf: string;
  horizon: string;
  rows: EvidenceRow[];
  changed: number;
  note: string;
  causality: string;
  statusNote: string;
}

/**
 * Величины, которые печатает таблица поручения — те же четыре смысла, что у
 * прототипа (отчёты, проверено, после срока без отчёта, разрыв по воде), плюс
 * «обязательств в основе». Разрыв по воде на каноне — ЧИСЛО ОБЯЗАТЕЛЬСТВ, а не
 * кубометры: подпись берётся у сервера, а не у прототипа.
 */
export const EVIDENCE_FIELDS = ['reported', 'verified', 'overdue', 'water_gap'] as const;

export function toEvidence(diff: AssignmentDiff): AssignmentEvidenceView {
  return {
    snapshotAsOf: diff.snapshot_as_of,
    horizon: diff.horizon,
    rows: diff.rows.map((row) => ({
      field: row.field,
      label: row.label,
      before: row.before ?? null,
      after: row.after ?? null,
      delta: row.delta ?? null,
    })),
    changed: diff.changed,
    note: diff.note,
    causality: diff.causality_note,
    statusNote: diff.status_note,
  };
}

export async function loadAssignmentEvidence(
  id: number,
  params: QueryParams,
): Promise<AssignmentEvidenceView> {
  return toEvidence(await api.assignmentDiff(id, params));
}

/** Адрес экрана поручений с формой, открытой по ситуации. */
export function assignFromSituationHref(situation: {
  key: string | null | undefined;
  title: string;
  explanation: string;
  farmId: string;
}): string | null {
  if (!situation.key) return null;
  const query = new URLSearchParams({
    situation: situation.key,
    title: situation.title,
    body: situation.explanation,
    farm: situation.farmId,
  });
  return `/ops/assignments?${query.toString()}`;
}

export function isServerAssignment(row: OverlayAssignment): row is ServerAssignment {
  return 'server' in row && typeof (row as ServerAssignment).server?.id === 'number';
}
